import React, { useEffect } from 'react';
import styles from './purchaseForm.module.scss';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import restFactory from 'v2/utils/restFactory';
import { toast } from 'react-toastify';
import { useMutation } from 'react-query';
import { DataProduct, DataProductPurchase } from 'v2/models';
import { PurchaseForm } from 'v2/utils/dataProductsUtils';
import FormModal from 'v2/common/FormModal/FormModal';
import { DATE_FORMAT } from 'utils/constants';
import PurchasesForm from './PurchasesForm';
import * as yup from 'yup';
import moment from 'moment';
import { PostProcessKeys, TranslationKeys } from 'translations';
import { useTranslation } from 'react-i18next';

interface EditPurchasesModalProps {
  dataProduct: DataProduct;
  onClose: (dataProduct?: DataProduct) => void;
}

const PurchaseFieldSchema = yup.object().shape({
  id: yup.mixed().optional(),
  opt_out_date: yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr))
    .when('renewal_date', (renewalDate, schema) => {
      return schema.test({
        test: (optOutDate: string) => {
          if (optOutDate && renewalDate) {
            return moment(optOutDate).isBefore(renewalDate);
          }
          return true;
        },
      });
    }),
  note: yup.string().required(),
  purchase_date: yup.date().required(),
  renewal_date: yup.date().min(yup.ref('purchase_date')).required(),
  timescale: yup.string().required(),
  cost: yup.object().shape({
    unit: yup.mixed().required(),
    value: yup.string().required(),
  }),
});

export const purchaseFormSchema = yup.object().shape({
  purchases: yup.array().of(PurchaseFieldSchema),
});

export const getPurchaseDefaultValue = (
  purchases: DataProductPurchase[] = []
): PurchaseForm => ({
  purchases: purchases.map((value) => ({
    ...value,
    purchase_date: moment(value.purchase_date).format(DATE_FORMAT.STANDARD),
    renewal_date: moment(value.renewal_date).format(DATE_FORMAT.STANDARD),
    opt_out_date: value.opt_out_date
      ? moment(value.opt_out_date).format(DATE_FORMAT.STANDARD)
      : null,
    cost: { value: `${value.cost}`, unit: `${value.currency}` },
  })),
});

const EditPurchasesModal = ({
  dataProduct,
  onClose,
}: EditPurchasesModalProps) => {
  const { t } = useTranslation();
  const formContext = useForm<PurchaseForm>({
    mode: 'onChange',
    resolver: yupResolver(purchaseFormSchema),
    defaultValues: getPurchaseDefaultValue(dataProduct.purchases),
  });
  const {
    reset,
    formState: { isValid },
  } = formContext;

  useEffect(() => {
    reset(getPurchaseDefaultValue(dataProduct.purchases));
  }, [dataProduct.purchases]);

  const { isLoading, mutate } = useMutation(
    async (purchases: DataProductPurchase[]) => {
      return await restFactory.patch<DataProduct>(
        `/dj/api/v1/data_product/${dataProduct.id}/`,
        { purchases }
      );
    },
    {
      onSuccess: (data) => {
        onClose(data);
        toast.success('Purchases updated successfully.');
      },
      onError: () => {
        toast.error(
          `Error updating ${t(TranslationKeys.dataProduct_one)} purchases.`
        );
      },
    }
  );

  const closeModal = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: PurchaseForm) => {
    if (isValid) {
      const body: DataProductPurchase[] = data.purchases.map((value) => ({
        ...value,
        id: value.id || undefined,
        cost: parseInt(value.cost.value),
        currency: `${value.cost.unit}`,
        purchase_date: moment(value.purchase_date).format(),
        renewal_date: moment(value.renewal_date).format(),
        opt_out_date: value.opt_out_date
          ? moment(value.opt_out_date).format()
          : null,
      }));
      mutate(body);
    }
  };

  return (
    <FormModal
      title={`Edit ${t(TranslationKeys.dataProduct_one, {
        postProcess: PostProcessKeys.TitleCase,
      })} Purchase History`}
      onClose={closeModal}
      onSubmit={onSubmit}
      isLoading={isLoading}
      formContext={formContext}
      className={styles.modalBody}
    >
      <PurchasesForm />
    </FormModal>
  );
};

export default EditPurchasesModal;
